import { TabPanel } from "@mui/lab";
import { useCallback, useMemo, useState } from "react";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import PageTabs from "../../../common/PageTabs";
import TabLabelWithCount from "../../../common/TabLabelWithCount";
import { getInitialFilterState } from "../../../common/filters/filterState";
import ContactsActionsToolbar from "./ContactsActionsToolbar";
import ContactsTab from "./ContactsTab";
import {
  filterDefinitionsForAllContacts,
  filterDefinitionsForNotInvitedContacts,
  searchDefinition,
} from "./filters/filterDefinitions";

const contactsTabs = ["all", "notInvited"] as const;
type ContactsTabValue = (typeof contactsTabs)[number];

const tabPanelSx = { height: "100%", width: "100%", m: 0, px: 3, py: 2.5 };

const FundStructureContactsPage = () => {
  usePageTitle("Contacts");

  const { clientCode, investorPortalApp } = useClientContext();

  const displayedTabs = useMemo<readonly ContactsTabValue[]>(
    () => (investorPortalApp ? contactsTabs : ["all"]),
    [investorPortalApp]
  );

  const [tab, setTab] = useState<ContactsTabValue>("all");

  const [filterStateAll, setFilterStateAll] = useState(() =>
    getInitialFilterState(`${clientCode}_contacts_filter_v3`, filterDefinitionsForAllContacts, [], [], searchDefinition)
  );

  const [filterStateNotInvited, setFilterStateNotInvited] = useState(() =>
    getInitialFilterState(
      `${clientCode}_not_invited_contacts_filter_v3`,
      filterDefinitionsForNotInvitedContacts,
      [],
      [],
      searchDefinition
    )
  );

  const getNotInvitedCount = useCallback(
    () =>
      adminApi.searchContacts({
        fieldIds: [],
        includeFundInvestors: false,
        includeInboxData: false,
        isNotInvited: true,
      }),
    []
  );

  const [notInvitedCountResp, , { fetch: fetchNotInvitedCount }] = useFetch(getNotInvitedCount, {
    disabled: !investorPortalApp,
  });

  const notInvitedCount = notInvitedCountResp?.total;

  const labels = [
    "All Contacts",
    <TabLabelWithCount
      key="not_invited"
      label="Not Invited"
      count={notInvitedCount ?? 0}
      total={notInvitedCount ?? 0}
      isActive={tab === "notInvited"}
      hideCounter={notInvitedCount === undefined}
    />,
  ];

  return (
    <>
      <GeneralPageHeader title="Contacts" hideBorderLine>
        <ContactsActionsToolbar />
      </GeneralPageHeader>
      <PageTabs tabs={displayedTabs} labels={labels} value={tab} onChange={setTab}>
        <TabPanel value="all" sx={tabPanelSx}>
          <ContactsTab
            filterState={filterStateAll}
            setFilterState={setFilterStateAll}
            onContactsChanged={fetchNotInvitedCount}
          />
        </TabPanel>
        {displayedTabs.includes("notInvited") && (
          <TabPanel value="notInvited" sx={tabPanelSx}>
            <ContactsTab
              filterState={filterStateNotInvited}
              setFilterState={setFilterStateNotInvited}
              notInvitedOnly
              excludedColumns={["invitationSentAt", "lastActivity"]}
              onContactsChanged={fetchNotInvitedCount}
            />
          </TabPanel>
        )}
      </PageTabs>
    </>
  );
};

export default FundStructureContactsPage;
